/**
 * ResearchResultRenderer
 *
 * Renders structured research synthesis JSON as a clean, readable UI
 * that adapts its structure to the content - only meaningful sections
 * are shown.  Typography and colour come from the brand tokens (font-sans
 * + fg/surface/hairline), never hardcoded families or hex.
 *
 * Falls back gracefully if keys are missing - renders whatever is present.
 */

import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  HiChevronDown,
  HiOutlineCheckCircle,
  HiOutlineExclamationTriangle,
  HiOutlineBookOpen,
  HiOutlineClipboard,
  HiOutlineCheck,
} from 'react-icons/hi2';
import { MemoPayload } from '../Research/types';

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

const confidenceColor: Record<string, string> = {
  high: 'bg-success-50 text-success-700 dark:bg-success-900/30 dark:text-success-400 border-success-200 dark:border-success-800',
  moderate: 'bg-warning-50 text-warning-700 dark:bg-warning-900/30 dark:text-warning-400 border-warning-200 dark:border-warning-800',
  low: 'bg-danger-50 text-danger-700 dark:bg-danger-900/30 dark:text-danger-400 border-danger-200 dark:border-danger-800',
};

const weightColor: Record<string, string> = {
  binding: 'bg-info-50 text-info-700 dark:bg-info-900/30 dark:text-info-300 border-info-200 dark:border-info-800',
  // Persuasive is secondary authority - champagne emphasis, not a second hue.
  persuasive: 'bg-champagne/10 text-fg-body border-champagne/40',
};

/* ------------------------------------------------------------------ */
/*  Sub-components                                                     */
/* ------------------------------------------------------------------ */

const Badge: React.FC<{ text: string; className?: string }> = ({ text, className }) => (
  <span className={`inline-block px-2 py-0.5 text-[11px] font-medium rounded border ${className ?? 'bg-surface-sunken text-fg-muted border-hairline-soft'}`}>
    {text}
  </span>
);

/** Collapsible section with smooth animation */
const Disclosure: React.FC<{
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
  badge?: React.ReactNode;
}> = ({ title, children, defaultOpen = false, badge }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(defaultOpen);
  const [copied, setCopied] = useState(false);
  const inner = useRef<HTMLDivElement>(null);
  const [h, setH] = useState<number | undefined>(defaultOpen ? undefined : 0);

  useEffect(() => {
    if (!inner.current) return;
    if (open) {
      const sh = inner.current.scrollHeight;
      setH(sh);
      const t = setTimeout(() => setH(undefined), 300);
      return () => clearTimeout(t);
    } else {
      const sh = inner.current.scrollHeight;
      setH(sh);
      requestAnimationFrame(() => setH(0));
    }
  }, [open]);

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!inner.current) return;
    navigator.clipboard.writeText(inner.current.innerText);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="glass rounded-lg shadow-sm overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2.5 w-full px-4 py-2.5 text-start transition-colors duration-200 ease-slide hover:bg-surface-sunken/60"
      >
        <HiChevronDown className={`w-3.5 h-3.5 text-fg-muted transition-transform duration-300 ease-slide ${open ? 'rotate-0' : '-rotate-90'}`} />
        <span className="flex-1 text-[13px] font-semibold text-fg-strong">{title}</span>
        {badge}
        <button
          onClick={handleCopy}
          className="p-1 text-fg-muted hover:text-fg-strong transition-colors duration-200"
          title={t('Copy')}
        >
          {copied ? <HiOutlineCheck className="w-3.5 h-3.5 text-success-500" /> : <HiOutlineClipboard className="w-3.5 h-3.5" />}
        </button>
      </button>
      <div
        ref={inner}
        style={{ height: h !== undefined ? `${h}px` : 'auto' }}
        className="transition-[height] duration-300 ease-slide overflow-hidden"
      >
        <div className="px-4 pb-3 pt-1 text-[13px] leading-relaxed text-fg-body space-y-3">{children}</div>
      </div>
    </div>
  );
};

const CitationChip: React.FC<{ citation: string; weight?: string; onOpenCitation?: (url: string) => void }> = ({
  citation,
  weight,
  onOpenCitation,
}) => (
  <button
    onClick={() => onOpenCitation?.(citation)}
    className="inline-flex items-center gap-1.5 px-2 py-0.5 text-[11px] font-medium rounded-full border border-hairline-soft bg-surface-sunken text-fg-body hover:text-champagne hover:border-champagne/50 transition-colors duration-200 ease-slide cursor-pointer"
    title={`View ${citation}`}
  >
    <HiOutlineBookOpen className="w-3 h-3" />
    <span className="max-w-[200px] truncate">{citation}</span>
    {weight && <Badge text={weight} className={weightColor[weight] || ''} />}
  </button>
);

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

interface Props {
  /** Shared engine schema - see `components/Research/types.ts`. */
  data: MemoPayload;
  onOpenCitation?: (url: string) => void;
}

const ResearchResultRenderer: React.FC<Props> = ({ data, onOpenCitation }) => {
  const { t } = useTranslation();
  const es = data.executive_summary;
  const da = data.detailed_analysis;
  const ai = data.authority_integration;
  const ja = data.jurisdictional_analysis;
  const conflicts = data.conflicting_authorities;
  const uncertainty = data.areas_of_uncertainty_and_development;
  const gaps = data.gaps_and_limitations;
  const pi = data.practical_implications;

  return (
    <div className="research-result space-y-3 reveal-item font-sans">
      {/* ── Direct Answer ──────────────────────────────────── */}
      {es && (
        <div className="space-y-2.5">
          {/* Direct answer as flowing prose */}
          {es.direct_answer && (
            <p className="text-[14px] leading-relaxed text-fg-body">
              {es.direct_answer}
            </p>
          )}

          {/* Key findings as concise bullets */}
          {es.key_findings && es.key_findings.length > 0 && (
            <ul className="space-y-1 ms-0">
              {es.key_findings.map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-[13px] text-fg-body">
                  <HiOutlineCheckCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-success-500" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          )}

          {/* Confidence + uncertainty inline */}
          {(es.confidence_level || es.areas_of_uncertainty) && (
            <div className="flex items-start gap-2 flex-wrap">
              {es.confidence_level && (
                <Badge
                  text={`${es.confidence_level.charAt(0).toUpperCase() + es.confidence_level.slice(1)} confidence`}
                  className={confidenceColor[es.confidence_level] || ''}
                />
              )}
              {es.areas_of_uncertainty && (
                <span className="text-[12px] text-fg-muted flex items-start gap-1">
                  <HiOutlineExclamationTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-warning-500" />
                  {es.areas_of_uncertainty}
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Detailed Analysis ─────────────────────────────── */}
      {da && Object.keys(da).length > 0 && (
        <div className="space-y-2">
          {Object.entries(da).map(([key, section]) => {
            if (!section || typeof section !== 'object') return null;
            const hasAuthorities = section.key_authorities && section.key_authorities.length > 0;
            return (
              <Disclosure
                key={key}
                title={section.topic || key.replace(/_/g, ' ')}
                defaultOpen={true}
              >
                <p className="leading-relaxed">{section.content}</p>

                {hasAuthorities && (
                  <div className="mt-2 space-y-2">
                    {section.key_authorities!.map((auth, i) => (
                      <div key={i} className="ps-3 border-s-2 border-info-200 dark:border-info-800 space-y-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <CitationChip citation={auth.citation} weight={auth.weight} onOpenCitation={onOpenCitation} />
                        </div>
                        {auth.principle && <p className="text-[13px]">{auth.principle}</p>}
                        {auth.application_notes && (
                          <p className="text-[12px] text-fg-muted">{auth.application_notes}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </Disclosure>
            );
          })}
        </div>
      )}

      {/* ── Authority Integration (only if substantive) ───── */}
      {ai && (ai.binding_authorities?.citations?.length || ai.persuasive_authorities?.citations?.length) ? (
        <Disclosure title={t('Authority integration')}>
          {ai.binding_authorities?.summary && (
            <div className="space-y-1.5">
              <p className="text-[13px]"><span className="font-semibold">{t('Binding:')}</span> {ai.binding_authorities.summary}</p>
              {ai.binding_authorities.citations.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {ai.binding_authorities.citations.map((c, i) => (
                    <CitationChip key={i} citation={c} weight="binding" onOpenCitation={onOpenCitation} />
                  ))}
                </div>
              )}
            </div>
          )}
          {ai.persuasive_authorities?.summary && ai.persuasive_authorities.citations.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-[13px]"><span className="font-semibold">{t('Persuasive:')}</span> {ai.persuasive_authorities.summary}</p>
              <div className="flex flex-wrap gap-1.5">
                {ai.persuasive_authorities.citations.map((c, i) => (
                  <CitationChip key={i} citation={c} weight="persuasive" onOpenCitation={onOpenCitation} />
                ))}
              </div>
            </div>
          )}
        </Disclosure>
      ) : null}

      {/* ── Jurisdictional Analysis (condensed) ───────────── */}
      {ja && (ja.applicable_frameworks || ja.variations_by_jurisdiction) && (
        <Disclosure title={`${t('Jurisdiction')}: ${ja.primary_jurisdiction || ''}`}>
          {ja.applicable_frameworks && <p className="text-[13px]">{ja.applicable_frameworks}</p>}
          {ja.variations_by_jurisdiction && ja.variations_by_jurisdiction !== 'N/A' && (
            <p className="text-[12px] text-fg-muted">{ja.variations_by_jurisdiction}</p>
          )}
        </Disclosure>
      )}

      {/* ── Conflicting Authorities ───────────────────────── */}
      {conflicts && conflicts.length > 0 && (
        <Disclosure
          title={t('Conflicting authorities')}
          badge={<Badge text={`${conflicts.length}`} className="bg-warning-50 text-warning-700 dark:bg-warning-900/30 dark:text-warning-400 border-warning-200 dark:border-warning-800" />}
        >
          <ul className="space-y-1">
            {conflicts.map((c, i) => (
              <li key={i} className="flex items-start gap-2 text-[13px]">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-warning-400 flex-shrink-0" />
                {typeof c === 'string' ? c : (c as any).description || JSON.stringify(c)}
              </li>
            ))}
          </ul>
        </Disclosure>
      )}

      {/* ── Areas of Uncertainty ───────────────────────────── */}
      {uncertainty && uncertainty.length > 0 && (
        <Disclosure title={t('Areas of uncertainty')}>
          {uncertainty.map((u, i) => (
            <div key={i} className="space-y-1 ps-3 border-s-2 border-warning-200 dark:border-warning-800">
              <p className="font-semibold text-[13px]">{u.area}</p>
              {u.current_status && <p className="text-[13px]">{u.current_status}</p>}
              {u.implications && <p className="text-[12px] text-fg-muted">{u.implications}</p>}
              {u.relevant_authorities && u.relevant_authorities.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {u.relevant_authorities.map((a, j) => (
                    <CitationChip key={j} citation={a} onOpenCitation={onOpenCitation} />
                  ))}
                </div>
              )}
            </div>
          ))}
        </Disclosure>
      )}

      {/* ── Gaps (only if substantive - no boilerplate) ───── */}
      {gaps && (
        (gaps.information_gaps && !['none', 'n/a', ''].includes(gaps.information_gaps.toLowerCase().trim())) ||
        (gaps.recommendations_for_deeper_research && !['none', 'n/a', ''].includes(gaps.recommendations_for_deeper_research.toLowerCase().trim()))
      ) && (
        <Disclosure title={t('Gaps & recommendations')}>
          {gaps.information_gaps && !['none', 'n/a', ''].includes(gaps.information_gaps.toLowerCase().trim()) && (
            <p className="text-[13px]">{gaps.information_gaps}</p>
          )}
          {gaps.limitation_summary && !['none', 'n/a', ''].includes(gaps.limitation_summary.toLowerCase().trim()) && (
            <p className="text-[13px] text-fg-muted">{gaps.limitation_summary}</p>
          )}
          {gaps.recommendations_for_deeper_research && !['none', 'n/a', ''].includes(gaps.recommendations_for_deeper_research.toLowerCase().trim()) && (
            <div className="p-2.5 bg-surface-sunken/70 border border-hairline-soft rounded-lg text-[12px] text-fg-body">
              <span className="font-semibold">{t('Recommendation')}:&nbsp;</span>
              {gaps.recommendations_for_deeper_research}
            </div>
          )}
        </Disclosure>
      )}

      {/* ── Practical Implications (only meaningful entries) ─ */}
      {pi && (() => {
        const boilerplate = ['n/a', 'none', 'not applicable', 'not applicable due to lack of specific legal authorities.', ''];
        const entries = Object.entries(pi).filter(
          ([, v]) => v && !boilerplate.includes(v.toLowerCase().trim())
        );
        if (entries.length === 0) return null;
        return (
          <Disclosure title={t('Practical implications')}>
            <dl className="space-y-2">
              {entries.map(([key, val]) => (
                <div key={key}>
                  <dt className="text-[12px] font-semibold text-fg-muted">
                    {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </dt>
                  <dd className="text-[13px]">{val}</dd>
                </div>
              ))}
            </dl>
          </Disclosure>
        );
      })()}
    </div>
  );
};

export default ResearchResultRenderer;
